import { parseApiError } from "./parseApiError";

export type AuthHeadersFn = () => Promise<HeadersInit>;
/**
 * Called when the backend responds 401. Receives the original Response so
 * the handler can inspect the body (e.g. HH-200 Unit 28 idle-timeout) and
 * branch on it. Implementations clone the response if they need to read it.
 */
export type UnauthorizedFn = (response?: Response) => Promise<never>;

/**
 * Optional provider for the PDPL/GDPR audit-context headers consumed by
 * hiya-backend's `hiya_audit` middleware. Kept as an opt-in fourth
 * argument so portals adopt it independently of how Cognito tokens are
 * minted. Standard implementation lives in `./auditContext`.
 */
export type AuditContextHeadersFn = () => Promise<Record<string, string>>;

export function createApiFetch(
  apiBase: string,
  getAuthHeaders: AuthHeadersFn,
  handleUnauthorized: UnauthorizedFn,
  getAuditContextHeaders?: AuditContextHeadersFn,
) {
  return async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const [authHeaders, auditHeaders] = await Promise.all([
      getAuthHeaders(),
      getAuditContextHeaders ? getAuditContextHeaders() : Promise.resolve({}),
    ]);
    const response = await fetch(`${apiBase}${path}`, {
      ...options,
      headers: { ...authHeaders, ...auditHeaders, ...options.headers },
    });
    if (response.status === 401) return handleUnauthorized(response);
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(parseApiError(response.status, body));
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  };
}
